import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { ModalBase } from "./modal-base";
import { registerModal } from "../store/modal-store";
import {
  hasExpired,
  getFormattedCutoffDate,
  giftCardUrl,
} from "../store/holiday-store";
import { productData } from "../store/product-store";

/**
 * Holiday Shipping Warning Modal
 *
 * Shows when users try to add to cart after the holiday shipping cutoff date.
 * Offers two options:
 * 1. Continue with order (may not arrive in time)
 * 2. Purchase a gift card instead (instant delivery)
 *
 * Reads state from holiday-store signals.
 *
 * Usage in Liquid:
 * <holiday-shipping-modal></holiday-shipping-modal>
 */
@customElement("holiday-shipping-modal")
export class HolidayShippingModal extends ModalBase {
  @state() private _hasExpired = false;
  @state() private _cutoffDate: string | null = null;
  @state() private _giftCardUrl = "/products/gift-card";

  constructor() {
    super();
    this.name = "holiday-shipping";
    this.allowOutsideClick = false; // Force user to make a choice

    // Subscribe to holiday store signals
    effect(() => {
      this._hasExpired = hasExpired.value;
      this._cutoffDate = getFormattedCutoffDate();
      this._giftCardUrl = giftCardUrl.value;
    });

    // Register with modal orchestrator
    registerModal({
      name: "holiday-shipping",
      priority: 10, // Low priority (show first if needed)
      shouldShow: (context) => {
        // The cutoff date lives in the main product's store (set only by
        // product-provider), so this modal must only fire for the main product
        // CTA. Collection cards (e.g. recommended products) share this modal
        // chain but have no holiday data of their own — otherwise the main
        // product's cutoff would warn when adding an unrelated card to the cart.
        if (
          context.productId === undefined ||
          context.productId !== productData.value?.id
        ) {
          return false;
        }

        // Only show if past cutoff date
        if (!hasExpired.value) return false;

        // Don't show if button is disabled
        if (context.triggerButton?.disabled) return false;

        return true;
      },
      onApprove: (context) => {
        console.log("[holiday-shipping] User acknowledged warning, continuing");
      },
      onCancel: (context) => {
        console.log("[holiday-shipping] User cancelled add-to-cart");
      },
    });
  }

  private _handleGiftCard() {
    // Redirect to gift card product page
    window.location.href = this._giftCardUrl;
  }

  render() {
    if (!this._isOpen) return html``;

    return html`
      <div
        class="modal-overlay fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="holiday-shipping-title"
      >
        <div
          class="modal-dialog bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="text-center mb-4">
            <div class="inline-block p-3 bg-yellow-100 rounded-full mb-3">
              <svg
                class="w-8 h-8 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 id="holiday-shipping-title" class="text-xl font-bold text-gray-900 mb-2">
              Holiday Shipping Deadline Passed
            </h2>
          </div>

          <div class="text-center mb-6">
            <p class="text-sm text-gray-600 mb-3">
              The holiday shipping deadline was
              <strong>${this._cutoffDate}</strong>.
            </p>
            <p class="text-sm text-gray-700 mb-4">
              Orders placed now may not arrive before the holidays.
            </p>
            <p class="text-sm text-blue-600 font-medium">
              Consider purchasing a gift card for instant delivery!
            </p>
          </div>

          <div class="flex flex-col gap-3">
            <button
              class="btn-gift-card w-full px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              @click=${this._handleGiftCard}
              type="button"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Purchase a Gift Card
            </button>
            <button
              class="btn-primary w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              @click=${this.handleApprove}
              type="button"
            >
              Continue with Order Anyway
            </button>
            <button
              class="btn-secondary w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              @click=${this.handleCancel}
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    `;
  }
}
